import React from "react";
import { Plus, Home, Trees } from "lucide-react";

const GEO = {
  soggiorno: { x: 12, y: 12, w: 196, h: 150 },
  cucina: { x: 216, y: 12, w: 132, h: 150 },
  ingresso: { x: 12, y: 170, w: 84, h: 118 },
  bagno: { x: 104, y: 170, w: 104, h: 118 },
  camera1: { x: 216, y: 170, w: 132, h: 62 },
  camera2: { x: 216, y: 240, w: 132, h: 48 },
};

const BASE_H = 300;
const COLS = 3;
const CW = 108;
const CH = 62;
const GAP = 6;

const bandGeo = (i, yStart) => ({
  x: 12 + (i % COLS) * (CW + GAP),
  y: yStart + Math.floor(i / COLS) * (CH + GAP),
  w: CW,
  h: CH,
});

export const FloorPlan = ({ zones, activeZone, onSelect, onRemove, counts, areaByZone, imageByZone = {}, onAdd }) => {
  const presets = zones.filter((z) => GEO[z.key]);
  const extraInternal = zones.filter((z) => !GEO[z.key] && z.kind === "internal");
  const externals = zones.filter((z) => z.kind === "external");

  const rows = (n) => Math.ceil(n / COLS);
  const intStart = BASE_H + 18;
  const intH = extraInternal.length ? rows(extraInternal.length) * (CH + GAP) + 16 : 0;
  const extStart = intStart + intH + (externals.length ? 18 : 0);
  const extH = externals.length ? rows(externals.length) * (CH + GAP) + 16 : 0;
  const height = BASE_H + (intH || externals.length ? 18 : 0) + intH + (externals.length ? 18 + extH : 0);

  const Zone = ({ zone, g, dashed }) => {
    const active = activeZone === zone.key;
    const count = counts[zone.key] || 0;
    const area = areaByZone[zone.key] || 0;
    const photo = area > 0 ? imageByZone[zone.key] : null;
    return (
      <g>
        <clipPath id={`clip-${zone.key}`}>
          <rect x={g.x} y={g.y} width={g.w - 4} height={g.h} />
        </clipPath>
        <clipPath id={`photo-${zone.key}`}>
          <rect x={g.x} y={g.y} width={g.w} height={g.h} />
        </clipPath>
        <g
          data-testid={`zone-${zone.key}`}
          onClick={() => onSelect(zone.key)}
          className="room-shape"
        >
          {photo ? (
            <g clipPath={`url(#photo-${zone.key})`}>
              <image
                data-testid={`zone-photo-${zone.key}`}
                href={photo}
                x={g.x}
                y={g.y}
                width={g.w}
                height={g.h}
                preserveAspectRatio="xMidYMid slice"
              />
              <rect
                x={g.x}
                y={g.y}
                width={g.w}
                height={g.h}
                fill={active ? "#1B2A20" : "#000000"}
                opacity={active ? 0.42 : 0.38}
              />
              {active && (
                <rect x={g.x} y={g.y} width={g.w} height={g.h} fill="#4E7C59" opacity="0.22" />
              )}
              <rect
                x={g.x}
                y={g.y}
                width={g.w}
                height={g.h}
                fill="none"
                stroke={active ? "#8FBF6F" : dashed ? "#4E7C59" : "#1B2A20"}
                strokeWidth={active ? 3 : 1.5}
                strokeDasharray={dashed && !active ? "5 3" : undefined}
              />
            </g>
          ) : (
            <rect
              x={g.x}
              y={g.y}
              width={g.w}
              height={g.h}
              fill={active ? "#1B2A20" : count ? "rgba(78,124,89,0.12)" : "rgba(255,255,255,0.72)"}
              stroke={active ? "#4E7C59" : dashed ? "#4E7C59" : "#1B2A20"}
              strokeWidth={active ? 3 : 1.5}
              strokeDasharray={dashed && !active ? "5 3" : undefined}
            />
          )}
          <text
            x={g.x + 10}
            y={g.y + 22}
            fontSize={g.w < 120 ? 9 : 11}
            fontFamily="JetBrains Mono, monospace"
            letterSpacing="1.1"
            clipPath={`url(#clip-${zone.key})`}
            fill={photo || active ? "#F4F7F2" : "#1B2A20"}
          >
            {(() => {
              const max = Math.max(6, Math.floor((g.w - 24) / (g.w < 120 ? 5.6 : 6.9)));
              const label = zone.name.toUpperCase();
              return label.length > max ? `${label.slice(0, max - 1)}…` : label;
            })()}
          </text>
          <text
            x={g.x + 10}
            y={g.y + 38}
            fontSize="9.5"
            fontFamily="JetBrains Mono, monospace"
            fill={photo ? "#E3EBE2" : active ? "#9BB0A2" : "#64748B"}
          >
            {`${area.toLocaleString("it-IT", { maximumFractionDigits: 2 })} m²`}
          </text>
          {count > 0 && (
            <g>
              <circle cx={g.x + g.w - 16} cy={g.y + 16} r="9" fill="#4E7C59" />
              <text
                x={g.x + g.w - 16}
                y={g.y + 20}
                fontSize="10"
                textAnchor="middle"
                fill="#fff"
                fontFamily="JetBrains Mono, monospace"
              >
                {count}
              </text>
            </g>
          )}
        </g>
        <g
          data-testid={`remove-zone-${zone.key}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(zone.key);
          }}
          className="room-shape"
        >
          <rect
            x={g.x + g.w - 20}
            y={g.y + g.h - 20}
            width="16"
            height="16"
            fill={active ? "#4E7C59" : "#E2E8F0"}
          />
          <text
            x={g.x + g.w - 12}
            y={g.y + g.h - 8}
            fontSize="11"
            textAnchor="middle"
            fill={active ? "#fff" : "#1B2A20"}
            fontFamily="JetBrains Mono, monospace"
          >
            ×
          </text>
        </g>
      </g>
    );
  };

  const BandLabel = ({ y, text, color }) => (
    <text
      x="12"
      y={y}
      fontSize="8.5"
      letterSpacing="2"
      fontFamily="JetBrains Mono, monospace"
      fill={color}
    >
      {text}
    </text>
  );

  return (
    <div className="grid-paper border border-border p-4 sm:p-6">
      <svg viewBox={`0 0 360 ${height}`} className="h-auto w-full" role="img" aria-label="Piantina">
        <rect x="6" y="6" width="348" height="288" fill="none" stroke="#1B2A20" strokeWidth="3" />
        {presets.map((z) => (
          <Zone key={z.key} zone={z} g={GEO[z.key]} />
        ))}

        {extraInternal.length > 0 && (
          <>
            <BandLabel y={intStart - 6} text="AMBIENTI AGGIUNTI" color="#1B2A20" />
            {extraInternal.map((z, i) => (
              <Zone key={z.key} zone={z} g={bandGeo(i, intStart)} />
            ))}
          </>
        )}

        {externals.length > 0 && (
          <>
            <BandLabel y={extStart - 6} text="AREE ESTERNE" color="#4E7C59" />
            {externals.map((z, i) => (
              <Zone key={z.key} zone={z} g={bandGeo(i, extStart)} dashed />
            ))}
          </>
        )}
      </svg>

      <div className="mt-5 border-t border-border pt-4">
        <div className="label-xs mb-3 text-muted-foreground">
          Ambienti e aree del progetto — clicca per configurare
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="zone-list">
          {zones.map((z) => (
            <button
              key={z.key}
              data-testid={`zone-chip-${z.key}`}
              onClick={() => onSelect(z.key)}
              className={`flex items-center justify-between gap-2 border bg-white/70 px-3 py-2 text-left transition-colors hover:border-[#4E7C59] ${
                activeZone === z.key ? "border-[#4E7C59]" : "border-border"
              }`}
            >
              <span className="flex items-center gap-1.5 font-mono-t text-[11px] uppercase tracking-wider">
                {z.kind === "internal" ? (
                  <Home className="h-3 w-3 text-[#1B2A20]" />
                ) : (
                  <Trees className="h-3 w-3 text-[#4E7C59]" />
                )}
                {z.name}
              </span>
              <span className="font-mono-t text-xs text-muted-foreground">
                {(areaByZone[z.key] || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} m²
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4 label-xs text-muted-foreground">
          <span>× rimuove l'ambiente</span>
          <span className="text-[#4E7C59]">tratteggio = area esterna</span>
        </div>
        <button
          data-testid="btn-add-zone"
          onClick={onAdd}
          className="flex items-center gap-2 border-2 border-dashed border-[#1B2A20]/40 bg-white/60 px-4 py-2 label-xs transition-colors hover:border-[#4E7C59] hover:text-[#4E7C59]"
        >
          <Plus className="h-3.5 w-3.5" /> Aggiungi ambiente o area
        </button>
      </div>
    </div>
  );
};
